/**
 * CustomerBillingTab - Billing tab content for customer detail modal
 * Extracted from CustomersPage.js
 */
import React, { useMemo, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { jsPDF } from 'jspdf';
import { formatCHF } from '../../../utils/format';

const CustomerBillingTab = ({
  editFormData,
  pendingCharges,
  setInvoiceDialogOpen,
  onRemoveProduct
}) => {
  const formattedAppointments = useMemo(() => {
    return (pendingCharges?.appointments || []).map((appt) => ({
      ...appt,
      displayDate: (appt.date || appt.start_time)
        ? new Date(appt.date || appt.start_time).toLocaleDateString('de-DE', {
            weekday: 'long',
            day: '2-digit',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })
        : ''
    }));
  }, [pendingCharges?.appointments]);

  const totalCount = pendingCharges?.total_count || 0;
  const totalAmount = pendingCharges?.total_amount || 0;
  const products = pendingCharges?.products || [];

  const handleExportOpenItemsCsv = useCallback(() => {
    if (!formattedAppointments.length) return;
    const headers = ['Datum', 'Service', 'Trainer', 'Preis (CHF)'];
    const rows = formattedAppointments.map((appt) => [
      appt.displayDate || '',
      appt.service_name || '',
      appt.trainer_name || '',
      (appt.price ?? 0).toFixed(2)
    ]);
    const csv = [headers, ...rows].map((r) => r.join(';')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Offene_Posten_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  }, [formattedAppointments]);

  const handleExportOpenItemsPdf = useCallback(() => {
    if (!formattedAppointments.length) return;
    const doc = new jsPDF();
    const title = 'Offene Posten';
    let y = 15;
    doc.setFontSize(14);
    doc.text(title, 14, y);
    y += 8;
    doc.setFontSize(10);
    doc.text(`Anzahl: ${totalCount} | Gesamt: CHF ${formatCHF(totalAmount)}`, 14, y);
    y += 8;
    doc.setFontSize(9);
    doc.text('Datum | Service | Trainer | Preis', 14, y);
    y += 6;
    formattedAppointments.forEach((appt) => {
      const line = `${appt.displayDate || ''} | ${appt.service_name || ''} | ${appt.trainer_name || ''} | CHF ${formatCHF(appt.price ?? 0)}`;
      if (y > 280) {
        doc.addPage();
        y = 15;
      }
      doc.text(line, 14, y);
      y += 6;
    });
    doc.save(`Offene_Posten_${new Date().toISOString().split('T')[0]}.pdf`);
  }, [formattedAppointments, totalAmount, totalCount]);

  return (
    <div className="space-y-6">
      {editFormData.payment_timing === 'ende_monat' ? (
        <>
          {/* Pending Charges for Ende Monat Customers */}
          {pendingCharges && (totalCount > 0 || products.length > 0) ? (
            <div className="space-y-4">
              {/* Hinweis: Kunde wird über Zahler abgerechnet */}
              {pendingCharges.billed_via_payer_name && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800" data-testid="billing-payer-hint">
                  👨‍👩‍👧 Diese Trainings werden über die <strong>Sammelrechnung von {pendingCharges.billed_via_payer_name}</strong> abgerechnet.
                </div>
              )}
              {/* Summary Card */}
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-6" data-testid="billing-summary-card">
                <div className="flex items-start justify-between gap-4">
                  <h4 className="font-semibold text-lg text-orange-900 mb-4">📊 Zusammenfassung</h4>
                  <div className="flex flex-wrap gap-2" data-testid="billing-export-actions">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleExportOpenItemsPdf}
                      data-testid="billing-export-pdf"
                    >
                      PDF exportieren
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleExportOpenItemsCsv}
                      data-testid="billing-export-csv"
                    >
                      CSV exportieren
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white p-4 rounded-lg border border-orange-200">
                    <p className="text-sm text-orange-600">Offene Trainings</p>
                    <p className="text-3xl font-bold text-orange-800" data-testid="billing-pending-count">{totalCount}</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-orange-200">
                    <p className="text-sm text-orange-600">Gesamtbetrag</p>
                    <p className="text-3xl font-bold text-orange-800" data-testid="billing-pending-total">CHF {formatCHF(totalAmount)}</p>
                  </div>
                </div>
              </div>

              {/* Appointments List */}
              <div className="bg-white border rounded-lg p-6">
                <h4 className="font-semibold text-lg text-gray-900 mb-4" data-testid="billing-open-appointments-title">🗓️ Offene Trainings ({formattedAppointments.length})</h4>
                <div className="space-y-3 max-h-96 overflow-y-auto" data-testid="billing-open-appointments-list">
                  {formattedAppointments.map((appt) => (
                    <div
                      key={appt.id}
                      className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
                      data-testid={`billing-appointment-${appt.id}`}
                    >
                      <div className="flex-1">
                        <p className="font-medium text-gray-900" data-testid={`billing-appointment-service-${appt.id}`}>
                          {appt.service_name}
                          {appt.billed_for && (
                            <span className="ml-2 text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-2 py-0.5" data-testid={`billing-appointment-billedfor-${appt.id}`}>
                              für {appt.billed_for}
                            </span>
                          )}
                        </p>
                        {appt.trainer_name && (
                          <p className="text-sm text-gray-600" data-testid={`billing-appointment-trainer-${appt.id}`}>Trainer: {appt.trainer_name}</p>
                        )}
                        <p className="text-xs text-gray-500 mt-1" data-testid={`billing-appointment-date-${appt.id}`}>
                          {appt.displayDate}
                        </p>
                      </div>
                      <div className="text-right ml-4">
                        <p className="font-semibold text-orange-800 text-lg" data-testid={`billing-appointment-price-${appt.id}`}>
                          CHF {formatCHF(appt.price)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Produktverkäufe (Shakes etc.) */}
              {products.length > 0 && (
                <div className="bg-white border rounded-lg p-6">
                  <h4 className="font-semibold text-lg text-gray-900 mb-4" data-testid="billing-open-products-title">🛒 Offene Produkte ({products.length})</h4>
                  <div className="space-y-3 max-h-96 overflow-y-auto" data-testid="billing-open-products-list">
                    {products.map((p) => (
                      <div
                        key={p.id}
                        className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
                        data-testid={`billing-product-${p.id}`}
                      >
                        <div className="flex-1">
                          <p className="font-medium text-gray-900" data-testid={`billing-product-name-${p.id}`}>
                            {p.product_name}{p.quantity > 1 ? ` x${p.quantity}` : ''}
                            {p.billed_for && (
                              <span className="ml-2 text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-2 py-0.5">
                                für {p.billed_for}
                              </span>
                            )}
                          </p>
                          {p.date && (
                            <p className="text-xs text-gray-500 mt-1">{new Date(p.date).toLocaleDateString('de-DE')}</p>
                          )}
                        </div>
                        <div className="text-right ml-4 flex items-center gap-3">
                          <p className="font-semibold text-orange-800 text-lg" data-testid={`billing-product-price-${p.id}`}>
                            CHF {formatCHF(p.price)}
                          </p>
                          {onRemoveProduct && !p.billed_for && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => onRemoveProduct(p)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 px-2"
                              title="Posten entfernen / stornieren"
                              data-testid={`billing-product-remove-${p.id}`}
                            >
                              ✕ Entfernen
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Button */}
              <div className="bg-gray-50 border rounded-lg p-6">
                <p className="text-sm text-gray-600 mb-4">
                  Erstellen Sie jetzt eine Rechnung für alle offenen Trainings. Die Termine werden automatisch als &quot;abgerechnet&quot; markiert.
                </p>
                <Button
                  onClick={() => setInvoiceDialogOpen(true)}
                  className="w-full bg-teal-600 hover:bg-teal-700 text-white"
                  size="lg"
                  data-testid="billing-create-invoice-button"
                >
                  📄 Rechnung erstellen (CHF {totalAmount?.toFixed(2)})
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center py-12 bg-gray-50 rounded-lg border" data-testid="billing-empty-state">
              <div className="text-6xl mb-4">✅</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Keine offenen Abrechnungen</h3>
              <p className="text-gray-600">
                Dieser Kunde hat aktuell keine offenen Trainings, die abgerechnet werden müssen.
              </p>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-12 bg-blue-50 rounded-lg border border-blue-200" data-testid="billing-loading-state">
          <div className="text-6xl mb-4">ℹ️</div>
          <h3 className="text-xl font-semibold text-blue-900 mb-2">Keine Ende-Monat Abrechnung</h3>
          <p className="text-blue-700 mb-4">
            Dieser Kunde hat die Zahlungsart &quot;{editFormData.payment_timing === 'sofort' ? 'Sofort' : 'Täglich'}&quot; und wird direkt beim Check-in abgerechnet.
          </p>
          <p className="text-sm text-blue-600">
            Um Ende-Monat Abrechnungen zu sehen, ändern Sie die Zahlungsart auf &quot;Ende Monat&quot; im Einstellungen-Tab.
          </p>
        </div>
      )}
    </div>
  );
};

export default React.memo(CustomerBillingTab);
